import React from 'react';
import { Modal } from '@dropbox/dig-components/modal';
import { Button } from '@dropbox/dig-components/buttons';
import { Text } from '@dropbox/dig-components/typography';
import { useIntl, defineMessages } from 'react-intl';
import { getBrandName } from 'js/sign-components/common/brand';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import { useIsMutating } from '@tanstack/react-query';
import { useSignerContext } from 'signer-app/signer-experience/signer-experience';

const messages = defineMessages({
  eidFailed: {
    id: 'e3454e0d360e8998ba3d21fe88bb7c30ea79143c25a40eb7641079ff10f03a1a',
    description:
      'error message in modal of signer flow, shows when trying to continue with electronic ID verification process',
    defaultMessage: 'eID request failed',
  },

  finishWithEId: {
    id: 'signer.eid.modal.completeButton',
    description:
      'button text in modal of signer flow, when clicked continue with electronic ID verification process',
    defaultMessage: 'Continue',
  },

  cancelQES: {
    id: 'signer.qualified.modal.cancelButton',
    description:
      'button text in modal of singer flow, when clicked closes the modal',
    defaultMessage: 'Cancel',
  },

  almostDone: {
    id: 'signer.qualified.modal.title',
    description:
      'title text of modal in signer flow, when starting the qualified signature request',
    defaultMessage: 'Almost done!',
  },

  contentEId: {
    id: 'signer.eid.modal.description',
    description:
      'text in modal of signer flow, informing signer that they need to finish electronic ID verification',
    defaultMessage:
      'Complete with eID to finish signing the document. This should only take a few minutes. Signers will select a country and electronic ID provider for verification. <a>Learn more</a>',
  },

  acceptTermsOfService: {
    id: 'signer.qualified.modal.terms',
    description:
      'text in modal of signer flow, informing signer to accept the terms of service ',
    defaultMessage:
      'By selecting <b>{finishButtonText}</b>, I agree to the <a>{brandName} Terms</a> and to be legally bound by this document.',
  },

  loadingEId: {
    id: 'signer.eid.modal.loadingText',
    description:
      'loading text of modal in signer flow, when redirecting signer to eID verification page',
    defaultMessage:
      'Please wait a moment while we begin the verification process.',
  },
});

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
};

export const SignerEIdModal = ({ isOpen, onRequestClose }: Props) => {
  const intl = useIntl();
  const learnMoreLink = HfReactHelper.getFaqUrl('articles/19695045778189');
  const { finalize } = useSignerContext();
  const isMutating = useIsMutating() > 0;
  const closeAndFinalize = () => {
    onRequestClose();
    finalize();
  };

  const content = (
    <div>
      {intl.formatMessage(messages.contentEId, {
        a: (...chunks) => (
          <a href={learnMoreLink} target="_blank" rel="noopener noreferrer">
            {chunks}
          </a>
        ),
      })}
      <br />
      <br />
      {intl.formatMessage(messages.acceptTermsOfService, {
        finishButtonText: intl.formatMessage(messages.finishWithEId),
        brandName: getBrandName('S'),
        a: (...chunks) => (
          <a
            href={HfReactHelper.getWebflowUrl('terms')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {chunks}
          </a>
        ),
        b: (...chunks) => <b>{chunks}</b>,
      })}
    </div>
  );

  return (
    <Modal
      open={isOpen}
      isCentered
      withCloseButton={'close'}
      onRequestClose={onRequestClose}
      aria-labelledby="signer-eid-modal-title"
    >
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title id="signer-eid-modal-title">
          {intl.formatMessage(messages.almostDone)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Text variant="paragraph">{content}</Text>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onRequestClose} type="button" variant="opacity">
          {intl.formatMessage(messages.cancelQES)}
        </Button>
        <Button
          data-test-id="signer-eid-modal-submit"
          type="submit"
          variant="primary"
          onClick={closeAndFinalize}
          disabled={isMutating}
        >
          {intl.formatMessage(messages.finishWithEId)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
